import React, { useMemo, useState } from "react";
import type { EmissionTarget } from "api/emission-targets";
import { DefaultTable, DeleteDialog, Loading } from "components";
import { useEmissionTargets, useDeleteEmissionTarget, useNotify } from "hooks";
import { getTargetsTableColumns } from "./getTargetsTableColumns";

export interface TargetsSettingsTableProps {
  onEditTarget: (target: EmissionTarget) => void;
}

export const TargetsSettingsTable = ({ onEditTarget }: TargetsSettingsTableProps) => {
  const notify = useNotify();
  const { data: targets = [], isLoading } = useEmissionTargets();
  const { mutateAsync: deleteTarget } = useDeleteEmissionTarget();
  const [targetToDelete, setTargetToDelete] = useState<EmissionTarget | null>(null);

  const columns = useMemo(
    () =>
      getTargetsTableColumns(onEditTarget, (target) => setTargetToDelete(target)),
    [onEditTarget]
  );

  const handleDeleteConfirm = async () => {
    if (!targetToDelete) return;
    try {
      await deleteTarget(targetToDelete.id);
      notify.success("Target deleted successfully.");
      setTargetToDelete(null);
    } catch (err: unknown) {
      const message =
        typeof (err as { response?: { data?: string } })?.response?.data === "string"
          ? (err as { response: { data: string } }).response.data
          : "Failed to delete target.";
      notify.error(message);
    }
  };

  const handleDeleteCancel = () => {
    setTargetToDelete(null);
  };

  if (isLoading) {
    return <Loading />;
  }

  return (
    <>
      <DefaultTable<EmissionTarget>
        columns={columns}
        data={targets}
        enablePagination={false}
        enableColumnResizing
        defaultColumn={{ filterFn: "startsWith" }}
        tableContainerSx={{
          height: "calc(100vh - 260px)",
          minHeight: "calc(100vh - 260px)",
          maxHeight: "calc(100vh - 260px)",
          "& thead tr th": {
            borderBottom: "none",
            textAlign: "left",
            padding: "8px 12px",
            color: "#054254",
          },
          "& tbody tr td": { textAlign: "left", padding: "8px 12px" },
          "& tbody tr:first-of-type td": { borderTop: "1px solid #D9D9D9" },
          "& thead tr:first-of-type th:first-of-type > div": { justifyContent: "flex-start" },
          "& tbody tr td:first-of-type": { justifyContent: "flex-start", textAlign: "left", px: "12px" },
        }}
        muiTableHeadRowProps={{
          sx: {
            height: "auto",
            minHeight: "45px",
            alignItems: "flex-start",
            backgroundColor: "#F8FAFC",
            "& > th": {
              paddingTop: 1,
              paddingBottom: 1,
              verticalAlign: "top",
            },
          },
        }}
        muiTableHeadCellProps={{
          sx: {
            textAlign: "left",
            lineHeight: "30px",
            color: "#054254",
            "& .MuiBadge-root": { display: "none" },
            "&:first-of-type": { textAlign: "left", "& > div": { justifyContent: "flex-start" } },
          },
        }}
        muiTableHeadCellFilterTextFieldProps={{
          variant: "standard",
          size: "small",
          fullWidth: true,
          sx: {
            backgroundColor: "#F6F6F8",
            mt: 0.5,
            minHeight: "36px",
            "& .MuiInput-root": {
              backgroundColor: "#F6F6F8",
              "&::before": { borderBottomColor: "#D9D9D9" },
              "&::after": { borderBottomColor: "#525B73" },
            },
            "& .MuiInputBase-root": { backgroundColor: "#F6F6F8" },
          },
        }}
        muiTableBodyRowProps={{
          sx: {
            minHeight: "45px",
            height: "fit-content",
            backgroundColor: "white",
          },
        }}
      />
      {targetToDelete && (
        <DeleteDialog
          isOpen={!!targetToDelete}
          onCancel={handleDeleteCancel}
          onDeleteClick={handleDeleteConfirm}
          target="target"
          title="Delete Target?"
        />
      )}
    </>
  );
};
